import { useLeadsRetrieve, type LeadDetail } from '@gta/api-client'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { DetailSkeleton, ErrorState } from '@/components/console/states'
import { PageContainer } from '@/components/layout/page-container'
import { Button } from '@/components/ui/button'
import { asApiError, errorMessageOf, unwrapResponse } from '@/features/leads/api-utils'
import { LeadEditForm } from '@/features/leads/lead-edit-form'

/**
 * 编辑线索页。
 *
 * 文件名的 `$leadId_.` 后缀是 TanStack Router 的「不嵌进父路由」写法:
 * `/leads/$leadId/edit` 与 `/leads/$leadId` 是**平级**两页,详情页因此不必变成带 Outlet 的布局路由。
 *
 * 无筛选态 → 无 search schema,冷加载 `/leads/$leadId/edit` 直接可用(自己拉一次详情做预填)。
 */
export const Route = createFileRoute('/_authed/leads/$leadId_/edit')({
  component: LeadEditPage,
})

function LeadEditPage() {
  const { leadId } = Route.useParams()
  const query = useLeadsRetrieve(leadId)
  const lead = unwrapResponse<LeadDetail>(query.data)
  const notFound = asApiError(query.error)?.status === 404

  return (
    <PageContainer
      title={lead ? `编辑线索 · ${lead.customer.name}` : '编辑线索'}
      breadcrumbs={[{ label: '线索管理', to: '/leads' }, { label: leadId }, { label: '编辑' }]}
      description="可编辑面=客咨要素与负责人;阶段与获客渠道不在其中(状态机单一入口 + 归因锚不可改)。"
      actions={
        <Button variant="outline" asChild>
          <Link to="/leads/$leadId" params={{ leadId }} search={{ tab: 'activities' }}>
            <ArrowLeft />
            返回详情
          </Link>
        </Button>
      }
    >
      {query.isLoading && <DetailSkeleton fields={8} />}

      {query.isError && (
        <ErrorState
          title={notFound ? '线索不存在' : '线索详情加载失败'}
          message={
            notFound ? `资源号「${leadId}」在库中查不到,可能已被清理或链接有误。` : errorMessageOf(query.error)
          }
          action={
            <Button variant="outline" size="sm" asChild>
              <Link to="/leads" search={{}}>
                回到线索列表
              </Link>
            </Button>
          }
        />
      )}

      {lead && <LeadEditForm lead={lead} />}
    </PageContainer>
  )
}
